import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronLeft } from 'lucide-react';
import { BuySellAssetControllerStages, TBasicTokenInfo } from '@shared/types/frontend/assetPage';
import Image from 'next/image';
import { BuySellAssetPageSchema } from '@shared/zod-schemas/frontend';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import { NATIVE_ADDRESS } from '@shared/utils/config/networks';
import { MultichainSwapResult } from '@shared/types/actions';
import SlidingModal from '@/components/home/mobile/SlidingModal';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import SlippageConfigStage from '../../desktop/buySellAsset/stages/SlippageConfigStage';
import { useOrderHistory } from '@/hooks/db/useOrderHistory';
import { useSession } from '@/contexts/LitSessionProvider';
import { useBuySellForm } from '../../desktop/buySellAsset/hooks/useBuySellForm';
import SelectFromToList from '../../desktop/buySellAsset/components/SelectFromToList';
import MobileAmountKeypad from './components/MobileAmountKeypad';
import { validate } from '@/components/trade/swap-home/const';
import { useSwap } from '@/contexts/SwapProvider';
import PreSwapConfirmationModal from '@/components/trade/swap-home/PreSwapConfirmationModal';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import { useTokenData } from '@/hooks/asset/useTokenData';
import { toSpecialFixed } from '@/lib/misc/utils';
import TradeButtons from './TradeButtons';
import { useTouchHandlerName } from '@/hooks/utilities/use-mobile';

//if tokenData is not provided, need to have tokenAddressInput and network
const MobileBuySellController = ({
  tokenData,
  tokenAddressInput,
  network
}: {
  tokenData?: TBasicTokenInfo;
  tokenAddressInput?: string;
  network?: number;
}) => {
  const {
    PKP
  } = useSession();
  const {
    data: assetData
  } = useERC20Balance({
    evmAddresses: [PKP?.additionalEthAddress],
    solanaAddresses: [PKP?.solanaAddress],
    useGeniusAPI: true
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  //fallback fetch if tokenData is not available
  const {
    data: tokenDataFallBack
  } = useTokenData({
    tokenAddress: tokenAddressInput,
    tokenNetwork: network?.toString()
  });
  tokenData = tokenDataFallBack || tokenData;
  const toggleModal = () => {
    console.log('isModalOpen', isModalOpen);
    setIsModalOpen(!isModalOpen);
    form.resetField('tradeAmount');
    form.watch('tradeSide') === 'buy' ? form.resetField('fromToken') : form.resetField('toToken');
    if (stage !== BuySellAssetControllerStages.default) setStage(BuySellAssetControllerStages.default);
    return;
  };
  const {
    refetch: refetchOrderHistory
  } = useOrderHistory({
    cloudWallet: PKP?.additionalEthAddress,
    solanaAddress: PKP?.solanaAddress
  });
  const [stage, setStage] = useState(BuySellAssetControllerStages.default);
  const {
    form,
    swapResult,
    swapLoading,
    resetAssets,
    handleOnRefresh,
    handleSelectTab,
    handleAmountChange,
    onSubmit,
    isFetching,
    setIsFetching
  } = useBuySellForm({
    tokenData,
    assetData
  });
  const {
    setFromAssets,
    setToAssets,
    triggerQuote,
    fromAssets,
    toAssets,
    quoteStringInfo,
    quoteFetching
  } = useSwap();
  const [_formWatch, _watchTradeAmount, _watchTradeSide, _watchFromToken, _watchToToken] = [form.watch(), form.watch('tradeAmount'), form.watch('tradeSide'), form.watch('fromToken'), form.watch('toToken')];
  const _formIsValid = useMemo(() => {
    const _validateFields = BuySellAssetPageSchema.safeParse(_formWatch);
    if (_validateFields.error) {
      console.log('BuySellTabContent:_formIsValid:error', {
        e: _validateFields.error
      });
      return false;
    }
    if (_validateFields.success) {
      console.log('BuySellTabContent:_formIsValid:SUCCESS', {
        form: _formWatch
      });
      return true;
    }
  }, [_formWatch]);
  const _validateFromAndToAssets = validate(fromAssets, toAssets);
  const _disabledButton = useMemo(() => Boolean(isFetching ||
  // quoteValidationForStages?.loading ||
  !_formIsValid || !_validateFromAndToAssets.valid), [isFetching, _formIsValid, _validateFromAndToAssets
  // quoteValidationForStages
  ]);
  const tokenInAssetData = assetData?.balances.find(x => {
    if (x?.contract_address?.toLowerCase() === tokenData?.token?.address?.toLowerCase() && x.network == tokenData?.token?.networkId) {
      return true;
    }
    if (tokenData?.token?.address?.toLowerCase() == WrappedNativeTokens.get(tokenData?.token?.networkId)?.toLowerCase() && x.network == tokenData?.token?.networkId) {
      return true;
    }
    return false;
  });
  const tokenBalance = parseFloat(tokenInAssetData?.balance || '0');

  // on executing swap
  useEffect(() => {
    // swap success
    if ((swapResult as MultichainSwapResult)?.order_id) {
      form.watch('tradeSide') === 'buy' ? form.resetField('fromToken') : form.resetField('toToken');
      form.resetField('tradeAmount');
      resetAssets();
      refetchOrderHistory();
      // toggleModal();
      return;
    }
    // swap executing
    if ((swapResult as MultichainSwapResult)?.time) {
      setStage(BuySellAssetControllerStages.default);
      // toggleModal();
      return;
    }
    return () => {};
  }, [swapResult, swapLoading]);
  const assetInAssetData = assetData?.balances.find(x => (x.contract_address === form.watch('fromToken')?.contract_address || form.watch('fromToken')?.contract_address?.toLowerCase() == WrappedNativeTokens.get(form.watch('fromToken')?.network)?.toLowerCase() && x.contract_address?.toLowerCase() == NATIVE_ADDRESS?.toLowerCase()) && x.network == form.watch('fromToken')?.network);
  const touchHandlerName = useTouchHandlerName();
  return <div className='w-full ' data-sentry-component="MobileBuySellController" data-sentry-source-file="MobileBuySellController.tsx">
      <TradeButtons form={form} token={tokenData} tokenBalance={tokenBalance} handleSelectTab={handleSelectTab} setIsModalOpen={setIsModalOpen} data-sentry-element="TradeButtons" data-sentry-source-file="MobileBuySellController.tsx" />
      <SlidingModal isOpen={isModalOpen} onClose={() => toggleModal()} heightFit={true} className='pb-[0px!important]' data-sentry-element="SlidingModal" data-sentry-source-file="MobileBuySellController.tsx">
        <Form {...form} data-sentry-element="Form" data-sentry-source-file="MobileBuySellController.tsx">
          <form onSubmit={form.handleSubmit(onSubmit)} className={`flex flex-col justify-between gap-2`}>
            {stage === BuySellAssetControllerStages.error && quoteStringInfo?.message && validate(fromAssets, toAssets).valid && !quoteFetching && <>
                  <Button type='button' variant='blue' className='order-first rounded-full flex gap-2 items-center w-[74px] h-[31px] px-3 text-genius-cream' {...{
              [touchHandlerName]: () => {
                resetAssets();
                setIsFetching(false);
                setStage(BuySellAssetControllerStages.default);
              }
            }}>
                    <ChevronLeft className='w-[15px] h-[15px]' />
                    <p>Back</p>
                  </Button>
                  <div className='flex flex-col items-center gap-4 px-6'>
                    <Image alt='error' src='/images/idea-error.png' width={68} height={68} />
                    <h1 className='font-normal text-[36px] text-genius-cream/50 mt-14'>
                      Not Very Smart
                    </h1>
                    <p className='text-sm  text-genius-cream/50'>
                      Failed to Generate a quote! This one is on us. Please try increasing your
                      slippage below and refresh for new quotes.
                    </p>
                  </div>
                  <div className='grid gap-2'>
                    <Button type='button' variant='pink' className='rounded-full w-full h-[56px]' {...{
                [touchHandlerName]: () => {
                  setStage(BuySellAssetControllerStages.slippageConfig);
                }
              }}>
                      Increase Slippage
                    </Button>
                    <Button type='button' variant='blue' className='rounded-full w-full h-[56px]' {...{
                [touchHandlerName]: async () => {
                  handleOnRefresh();
                }
              }}>
                      Refresh
                    </Button>
                  </div>
                </>}
            {stage === BuySellAssetControllerStages.default && <div className='w-full h-full flex flex-col gap-4 justify-between'>
                <p className='self-center text-[20px]'>
                  {form.watch('tradeSide') === 'buy' ? 'Buy' : 'Sell'}
                </p>
                <MobileAmountKeypad form={form} maxBalance={assetInAssetData?.usd_value ? toSpecialFixed(assetInAssetData?.usd_value, 2).value : '0'} tradeSide={form.watch('tradeSide')} tokenSymbol={form.watch('tradeSide') === 'sell' ? form.watch('fromToken')?.contract_ticker_symbol : ''} handleAmountChange={handleAmountChange} setStage={setStage} />

                <div className='grid gap-2  p-2'>
                  {/* validation error message */}
                  {Boolean(_validateFromAndToAssets?.error !== '' && _validateFromAndToAssets?.error !== 'Swap') && <div className='flex gap-2 items-center justify-center my-2'>
                      <AlertCircle className='text-yellow-400/70 ' />
                      <span className='text-sm text-genius-cream/70'>
                        {_validateFromAndToAssets.error}
                      </span>
                    </div>}
                  {/* button to fetch quote and submit order
                   quoteValidationForStages?.success
                   */}
                  <Button variant='pink' className={` w-full h-[56px] flex items-center gap-2 rounded-full text-[16px] text-genius-indigo`} type={'button'} disabled={_disabledButton} {...{
                [touchHandlerName]: () => {
                  // if (assetsAndFormIsEqual) {
                  //   setStage(BuySellAssetControllerStages.confirmTx);
                  //   return;
                  // }

                  if (_formIsValid && _validateFromAndToAssets.valid) {
                    setFromAssets([{
                      ...(assetInAssetData as CovalentBalanceItem),
                      amountToUseStr: _watchTradeAmount,
                      amountToUse: Number(_watchTradeAmount)
                    }]);
                    setToAssets([_watchToToken as CovalentBalanceItem]);
                    triggerQuote();
                    setStage(BuySellAssetControllerStages.confirmTx);
                  }
                }
              }}>
                    Next
                  </Button>
                </div>
              </div>}
            {stage === BuySellAssetControllerStages.tokenList && <SelectFromToList form={form} tokenData={tokenData} assetData={assetData} setStage={setStage} />}
            {stage === BuySellAssetControllerStages.slippageConfig && <SlippageConfigStage setStage={setStage} />}
            {stage === BuySellAssetControllerStages.confirmTx && <>
                {!!fromAssets?.length && !!toAssets?.length && validate(fromAssets, toAssets).valid && <PreSwapConfirmationModal onClickBack={() => setStage(BuySellAssetControllerStages.default)} />}
              </>}
          </form>
        </Form>
      </SlidingModal>
    </div>;
};
export default MobileBuySellController;